import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { inboxApi } from './api'
import type { InboxAccount, InboxItemStatus } from './types'

function useImportRunningPoll() {
  const tenantId = useTenant()
  const qc = useQueryClient()
  return () => {
    const data = qc.getQueryData<{ accounts: InboxAccount[] }>(
      tenantKey(tenantId, 'inbox', 'accounts')
    )
    return (data?.accounts || []).some((a) => a.backfill_status === 'running') ? 4000 : false
  }
}

export function useInboxAccounts() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'accounts'),
    queryFn: () => inboxApi.listAccounts(),
    refetchInterval: (query) => {
      const accounts = query.state.data?.accounts || []
      return accounts.some((a) => a.backfill_status === 'running') ? 4000 : false
    },
  })
}

export function useInboxEmails(params?: {
  accountId?: string
  q?: string
  categoryId?: string
  limit?: number
}) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'emails', params || {}),
    queryFn: () => inboxApi.listEmails(params),
  })
}

export function useInboxEmail(emailId: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'email', emailId || 'none'),
    queryFn: () => inboxApi.getEmail(emailId as string),
    enabled: !!emailId,
  })
}

export function useInboxCategories() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'categories'),
    queryFn: () => inboxApi.listCategories(),
  })
}

export function useCategoryCounts(accountId?: string) {
  const tenantId = useTenant()
  const refetchInterval = useImportRunningPoll()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'category-counts', { accountId: accountId || null }),
    queryFn: () => inboxApi.categoryCounts(accountId),
    refetchInterval,
  })
}

export function useWatchRules() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'rules'),
    queryFn: () => inboxApi.listRules(),
  })
}

function useInboxInvalidate() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return (...parts: string[]) =>
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', ...parts) })
}

export function useCreateCategory() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (body: import('./types').CategoryPayload) => inboxApi.createCategory(body),
    onSuccess: () => {
      invalidate('categories')
      invalidate('category-counts')
    },
  })
}

export function useUpdateCategory() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; body: import('./types').CategoryPayload }) =>
      inboxApi.updateCategory(vars.id, vars.body),
    onSuccess: () => {
      invalidate('categories')
      invalidate('category-counts')
    },
  })
}

export function useDeleteCategory() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (id: string) => inboxApi.deleteCategory(id),
    onSuccess: () => {
      invalidate('categories')
      invalidate('category-counts')
    },
  })
}

export function useCreateRule() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (body: import('./types').WatchRulePayload) => inboxApi.createRule(body),
    onSuccess: () => invalidate('rules'),
  })
}

export function useUpdateRule() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; body: import('./types').WatchRulePayload }) =>
      inboxApi.updateRule(vars.id, vars.body),
    onSuccess: () => invalidate('rules'),
  })
}

export function useDeleteRule() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (id: string) => inboxApi.deleteRule(id),
    onSuccess: () => invalidate('rules'),
  })
}

export function useCreateBillDraft() {
  const invalidate = useInboxInvalidate()
  return useMutation({
    mutationFn: (itemId: string) => inboxApi.createBillDraft(itemId),
    onSuccess: () => {
      invalidate('items')
      invalidate('category-counts')
    },
  })
}

export function useUpdateAutoSync() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (vars: {
      id: string
      sync_interval_minutes: number
      auto_sync_enabled: boolean
      classification_mode?: import('./types').ClassificationMode
    }) =>
      inboxApi.updateAutoSync(vars.id, {
        sync_interval_minutes: vars.sync_interval_minutes,
        auto_sync_enabled: vars.auto_sync_enabled,
        classification_mode: vars.classification_mode,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'accounts') })
    },
  })
}

export function useEstimateOnboarding() {
  return useMutation({
    mutationFn: (vars: {
      id: string
      body: Pick<
        import('./types').OnboardingStartPayload,
        'horizon' | 'custom_start' | 'custom_end' | 'scope' | 'source' | 'keywords'
      >
    }) => inboxApi.estimateOnboarding(vars.id, vars.body),
  })
}

export function useStartOnboarding() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (vars: { id: string; body: import('./types').OnboardingStartPayload }) =>
      inboxApi.startOnboarding(vars.id, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'accounts') })
    },
  })
}

export function useImportOlder() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => inboxApi.importOlder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'accounts') })
    },
  })
}

export function useStartConnect() {
  return useMutation({
    mutationFn: () => inboxApi.startConnect(),
  })
}

export function useSyncAccount() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (vars: { id: string; maxResults?: number }) =>
      inboxApi.sync(vars.id, vars.maxResults),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'emails') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'accounts') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'items') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'category-counts') })
    },
  })
}

export function useDisconnectAccount() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => inboxApi.disconnect(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'accounts') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'emails') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'items') })
    },
  })
}

export function useInboxItems(params?: {
  status?: InboxItemStatus
  categoryId?: string
  limit?: number
}) {
  const tenantId = useTenant()
  const refetchInterval = useImportRunningPoll()
  return useQuery({
    queryKey: tenantKey(tenantId, 'inbox', 'items', params || {}),
    queryFn: () => inboxApi.listItems(params),
    refetchInterval,
  })
}

export function useConfirmItem() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => inboxApi.confirmItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'items') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'category-counts') })
    },
  })
}

export function useDismissItem() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => inboxApi.dismissItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'items') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'category-counts') })
    },
  })
}

export function useReextractEmail() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => inboxApi.reextractEmail(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'items') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'inbox', 'category-counts') })
    },
  })
}
